import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/PageHeader";
import { PhotoUpload } from "../../components/PhotoUpload";
import { PasswordStrength } from "../../components/PasswordStrength";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { api, apiError } from "../../lib/api";
import { BADGE_OPTIONS, LEVELS } from "./constants";
import { cn } from "../../lib/utils";

const schema = z.object({
  name: z.string().min(2, "Enter the full name"),
  email: z.string().min(1, "Email is required").email("Enter a valid email address"),
  password: z
    .string()
    .min(8, "At least 8 characters")
    .regex(/[A-Z]/, "Add an uppercase letter")
    .regex(/[a-z]/, "Add a lowercase letter")
    .regex(/[0-9]/, "Add a number")
    .regex(/[^A-Za-z0-9]/, "Add a special character"),
  phone: z.string().optional(),
  address: z.string().optional(),
});

export default function AdminClientNew() {
  const navigate = useNavigate();
  const [managers, setManagers] = useState([]);
  const [managerId, setManagerId] = useState("none");
  const [level, setLevel] = useState("Bronze");
  const [badges, setBadges] = useState(["Verified"]);
  const [photo, setPhoto] = useState(null);
  const [sendWelcome, setSendWelcome] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", password: "", phone: "", address: "" },
  });
  const password = watch("password");

  useEffect(() => {
    api
      .get("/api/admin/managers")
      .then(({ data }) => setManagers(data))
      .catch(() => {});
  }, []);

  const toggleBadge = (badge) =>
    setBadges((prev) => (prev.includes(badge) ? prev.filter((b) => b !== badge) : [...prev, badge]));

  const onSubmit = async (values) => {
    try {
      const { data } = await api.post(
        "/api/admin/clients",
        {
          ...values,
          email: values.email.trim().toLowerCase(),
          phone: values.phone || "",
          address: values.address || "",
          photo,
          managerId: managerId === "none" ? null : managerId,
          level,
          badges,
          status: "active",
        },
        { params: { sendWelcome } }
      );
      toast.success(`${data.name} added. You can now set their financials.`);
      navigate(`/admin/clients/${data.id}`);
    } catch (error) {
      toast.error(apiError(error, "Could not create the client"));
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="New client"
        subtitle="Create an investor account. Financial figures can be set right after creation."
        actions={
          <Button asChild variant="secondary">
            <Link to="/admin/clients" data-testid="admin-client-new-back">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to clients
            </Link>
          </Button>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <div className="grid gap-5 lg:grid-cols-2">
          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Identity &amp; credentials</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" {...register("name")} data-testid="admin-client-name-input" />
                {errors.name ? <p className="text-xs text-destructive">{errors.name.message}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  {...register("email")}
                  data-testid="admin-client-email-input"
                />
                {errors.email ? <p className="text-xs text-destructive">{errors.email.message}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Initial password</Label>
                <Input
                  id="password"
                  type="text"
                  autoComplete="new-password"
                  {...register("password")}
                  data-testid="admin-client-password-input"
                />
                {errors.password ? (
                  <p className="text-xs text-destructive">{errors.password.message}</p>
                ) : null}
                <PasswordStrength value={password} />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border surface-2 p-3">
                <div>
                  <p className="text-sm font-medium">Email the credentials</p>
                  <p className="text-xs text-muted-foreground">
                    Sends a welcome email with the sign-in details.
                  </p>
                </div>
                <Switch
                  checked={sendWelcome}
                  onCheckedChange={setSendWelcome}
                  data-testid="admin-client-send-welcome-switch"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Contact &amp; profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  placeholder="+91 98765 43210"
                  {...register("phone")}
                  data-testid="admin-client-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  rows={3}
                  {...register("address")}
                  data-testid="admin-client-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Profile photo</Label>
                <PhotoUpload value={photo} onChange={setPhoto} />
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Assignment, level &amp; badges</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-5 lg:grid-cols-3">
            <div className="space-y-2">
              <Label>Manager / upline</Label>
              <Select value={managerId} onValueChange={setManagerId}>
                <SelectTrigger data-testid="admin-client-manager-select">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {managers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name} ({m.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Level</Label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger data-testid="admin-client-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l) => (
                    <SelectItem key={l} value={l}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Badges</Label>
              <div className="flex flex-wrap gap-2">
                {BADGE_OPTIONS.map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => toggleBadge(b)}
                    className={cn(
                      "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                      badges.includes(b)
                        ? "border-primary bg-primary/15 text-primary"
                        : "border-border text-muted-foreground hover:text-foreground"
                    )}
                    data-testid={`admin-client-badge-${b.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={isSubmitting} data-testid="admin-client-create-submit">
            {isSubmitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Create client
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate("/admin/clients")}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
